// Настройки графики: «жизнь» в замке и на полях (жители, повозки, стройка), движение войск и флотов на карте,
// эффекты (вода, огонь, пульсация) и чёткость экрана. При первом запуске игра сама оценивает устройство:
// на хорошем всё включено, на слабом — экономный режим. Игрок меняет всё в Профиле → «Графика».
import i18n from '@/i18n';
import { useGame } from '@/store/game';
import { lowEndDevice } from './device';

export interface Graphics {
  /** Жители, повозки и стройка в замке и на полях. */
  life: boolean;
  /** Плавное движение войск и флотов на карте (выкл — позиции обновляются раз в секунду). */
  marches: boolean;
  /** Анимация воды, огня, штормов и пульсация отметок. */
  effects: boolean;
  /** Чёткость под экран телефона (выкл — вдвое меньше точек: быстрее и экономнее). */
  hiRes: boolean;
}

export type GraphicsPreset = 'high' | 'low';

export const GRAPHICS_PRESETS: Record<GraphicsPreset, Graphics> = {
  high: { life: true, marches: true, effects: true, hiRes: true },
  low: { life: false, marches: false, effects: false, hiRes: false },
};

const KEY = 'sk_gfx';
const listeners = new Set<(g: Graphics) => void>();
let current: Graphics | null = null;
/** Режим выбран автоматически и ещё уточняется замером настоящих кадров (игрок ничего не менял). */
let auto = false;

/**
 * Быстрая проверка (~0,12 с): сколько «кадров» как в замке (крупный фон с масштабом + 40 спрайтов) успевает
 * нарисовать устройство. Слабые и бюджетные телефоны (и эмуляторы без видеокарты) успевают мало.
 */
export function benchFrames(): number {
  try {
    const src = document.createElement('canvas');
    src.width = 1024;
    src.height = 1024;
    const s = src.getContext('2d');
    const cv = document.createElement('canvas');
    cv.width = 720;
    cv.height = 1280;
    const c = cv.getContext('2d', { alpha: false });
    if (!s || !c) return 99;
    const g = s.createLinearGradient(0, 0, 1024, 1024);
    g.addColorStop(0, '#123');
    g.addColorStop(1, '#c93');
    s.fillStyle = g;
    s.fillRect(0, 0, 1024, 1024);
    const start = performance.now();
    let frames = 0;
    while (performance.now() - start < 120) {
      c.drawImage(src, 0, 0, 1024, 1024, -(frames % 40), 0, 1400, 1400);
      for (let i = 0; i < 40; i++) c.drawImage(src, i * 7, i * 5, 64, 64, i * 17, i * 29, 48, 48);
      c.getImageData(0, 0, 1, 1); // дождаться отрисовки
      frames++;
    }
    return frames;
  } catch {
    return 99;
  }
}

/** Порог проверки: меньше стольких кадров за 0,12 с — экономный режим (с запасом: с видеокартой
 *  телефоны рисуют иначе, окончательно решает частота кадров в замке — reportFrameInterval). */
const SLOW_FRAMES = 4;

/** Оценка устройства при первом запуске. */
export function detectPreset(): GraphicsPreset {
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return 'low';
  if (mem !== undefined && mem <= 2) return 'low';
  if ((navigator.hardwareConcurrency ?? 8) <= 2) return 'low';
  const frames = benchFrames();
  console.info(`[graphics] проверка устройства: ${frames} кадров`);
  if (frames < SLOW_FRAMES) return 'low';
  // 3–4 ядра без явной слабости — полная графика (раньше такие телефоны считались слабыми целиком)
  return lowEndDevice() && (mem ?? 4) <= 3 ? 'low' : 'high';
}

function save(g: Graphics): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...g, auto }));
  } catch {
    // без хранилища — определяем при каждом запуске
  }
}

function load(): Graphics {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const { auto: a, ...g } = JSON.parse(raw) as Partial<Graphics> & { auto?: boolean };
      auto = !!a;
      return { ...GRAPHICS_PRESETS.high, ...g };
    }
  } catch {
    // нет хранилища или испорчено — определим заново
  }
  const preset = detectPreset();
  // Полную графику проверяем дальше на настоящих кадрах; экономную не повышаем сами
  auto = preset === 'high';
  const g = { ...GRAPHICS_PRESETS[preset] };
  save(g);
  return g;
}

export function graphics(): Graphics {
  if (!current) current = typeof window === 'undefined' ? { ...GRAPHICS_PRESETS.high } : load();
  return current;
}

/** Изменение от игрока: дальше автоматика режим не трогает. */
export function setGraphics(patch: Partial<Graphics>): void {
  auto = false;
  apply({ ...graphics(), ...patch });
}

function apply(g: Graphics): void {
  current = g;
  save(g);
  for (const fn of listeners) fn(g);
}

const samples: number[] = [];
/**
 * Медиана промежутка между кадрами экрана (мс), выше которой телефон не тянет «жизнь»: браузер просит
 * кадры 60 раз в секунду (16,7 мс); если растеризация не успевает, кадры приходят реже. 40 мс ≈ 25 кадров/с.
 */
const SLOW_INTERVAL_MS = 40;

/**
 * Замер при автоматическом режиме: 150 промежутков между кадрами, пока в замке идёт «жизнь». Телефон не
 * успевает (медиана дольше SLOW_INTERVAL_MS) — один раз включаем экономный режим и сообщаем об этом.
 */
export function reportFrameInterval(ms: number): void {
  if (!auto || !graphics().life || ms <= 0 || ms > 1000) return;
  samples.push(ms);
  if (samples.length < 150) return;
  const median = [...samples].sort((a, b) => a - b)[75]!;
  samples.length = 0;
  auto = false;
  console.info(`[graphics] кадры замка: ${median.toFixed(1)} мс между кадрами`);
  if (median > SLOW_INTERVAL_MS) {
    apply({ ...GRAPHICS_PRESETS.low });
    useGame.getState().toast(i18n.t('game:profile.graphics.autoLow'), 'info');
  } else save(graphics());
}

export function onGraphicsChange(fn: (g: Graphics) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Плотность точек холста: чёткость под экран или экономно (1×). */
export function canvasDpr(): number {
  return graphics().hiRes ? Math.min(2, window.devicePixelRatio || 1) : 1;
}

/** «Жизнь» в замке и на полях включена (и система не просит уменьшить движение). */
let reducedMotion: boolean | null = null;
export function lifeOn(): boolean {
  reducedMotion ??= !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  return graphics().life && !reducedMotion;
}

/** Какой набор сейчас включён (для подписи в настройках). */
export function currentPreset(): GraphicsPreset | 'custom' {
  const g = graphics();
  for (const p of ['high', 'low'] as const) {
    const v = GRAPHICS_PRESETS[p];
    if (v.life === g.life && v.marches === g.marches && v.effects === g.effects && v.hiRes === g.hiRes)
      return p;
  }
  return 'custom';
}
